import { useState } from 'react';
import { HandCoins, Plus } from 'lucide-react';
import { P } from '@stockpilot/shared';
import { useFormat } from '@/hooks/useAccess';
import {
  useAccountsQuery, useCreatePaymentMutation, useCustomerDepositsQuery, usePaymentMethodsQuery,
  usePaymentsQuery,
} from '@/api/endpoints';
import { apiErrorMessage } from '@/api/baseApi';
import { formatDate, formatMoney } from '@/lib/format';
import {
  Alert, Button, Card, EmptyState, ErrorState, Input, LoadingState, Modal, PageHeader,
  Pagination, Select, Table, Td, Th,
} from '@/components/ui';
import { Can } from '@/components/Can';
import { CustomerPicker } from '@/components/CustomerPicker';
import { SummaryCards } from '@/components/SummaryCards';

const emptyDeposit = { customerId: '', amount: '', methodId: '', accountId: '', reference: '', notes: '' };

/**
 * Money customers leave with the business to spend later.
 *
 * A deposit is a payment with no sale to settle: the money lands in the
 * account it was paid into, and the customer's balance goes into their favour.
 * They spend it by paying with "Store credit". Until then it is money the
 * business is holding for them, which is why the total is shown as owed.
 */
export function DepositsPage() {
  const format = useFormat();

  const [page, setPage] = useState(1);
  const [recentPage, setRecentPage] = useState(1);
  const [recording, setRecording] = useState(false);
  const [form, setForm] = useState(emptyDeposit);
  const [failure, setFailure] = useState<string | null>(null);

  const held = useCustomerDepositsQuery({ page, limit: 15 });
  const recent = usePaymentsQuery({ deposits: true, page: recentPage, limit: 10 });
  const { data: methods } = usePaymentMethodsQuery();
  const { data: accounts } = useAccountsQuery({ limit: 50 });
  const [createPayment, createState] = useCreatePaymentMutation();

  // Only a method that moves money can take a deposit: store credit would be
  // paying a customer with their own money, and "pay later" is no money at all.
  const takingMethods = (methods ?? []).filter(
    (method) => method.type !== 'CREDIT' && method.type !== 'STORE_CREDIT',
  );
  const chosenMethod = takingMethods.find((method) => method.id === form.methodId);

  const close = (): void => {
    setRecording(false);
    setForm(emptyDeposit);
    setFailure(null);
  };

  const submit = async (): Promise<void> => {
    setFailure(null);
    try {
      await createPayment({
        customerId: form.customerId,
        methodId: form.methodId,
        amount: Number(form.amount),
        ...(form.accountId ? { accountId: form.accountId } : {}),
        ...(form.reference ? { reference: form.reference } : {}),
        notes: form.notes ? `Deposit: ${form.notes}` : 'Deposit',
        // No allocations: that is what makes it a deposit.
        allocations: [],
      }).unwrap();
      close();
    } catch (error) {
      setFailure(apiErrorMessage(error, 'The deposit could not be recorded'));
    }
  };

  return (
    <>
      <PageHeader
        title="Deposits"
        description="Money customers have left with the business to spend later. They spend it by paying with Store credit."
        action={
          <Can permission={P.PAYMENT_CREATE}>
            <Button icon={Plus} onClick={() => setRecording(true)}>Record deposit</Button>
          </Can>
        }
      />

      {held.data?.summary && (
        <SummaryCards
          stats={[
            {
              label: 'Held for customers',
              value: formatMoney(String(held.data.summary.held), format),
              hint: 'owed back until it is spent',
            },
            { label: 'Customers in credit', value: String(held.data.summary.count) },
          ]}
        />
      )}

      <Card className="mb-6">
        <div className="border-b border-subtle p-4">
          <h2 className="text-sm font-semibold">Customers with money on account</h2>
          <p className="text-xs text-muted">
            What each one can still spend: deposits, and returns credited to their account, less
            what they have already bought with them.
          </p>
        </div>
        {held.isLoading ? (
          <LoadingState />
        ) : held.isError ? (
          <ErrorState message={apiErrorMessage(held.error)} onRetry={() => void held.refetch()} />
        ) : !held.data || held.data.items.length === 0 ? (
          <EmptyState
            icon={HandCoins}
            title="No customer has money on account"
            description="Record a deposit when a customer pays ahead of buying."
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Customer</Th>
                  <Th>Phone</Th>
                  <Th>Last deposit</Th>
                  <Th className="text-right">Available to spend</Th>
                </tr>
              </thead>
              <tbody>
                {held.data.items.map((customer) => (
                  <tr key={customer.id}>
                    <Td>
                      <p className="font-medium">{customer.name}</p>
                      <p className="text-xs text-muted">{customer.code}</p>
                    </Td>
                    <Td>{customer.phone ?? <span className="text-muted">—</span>}</Td>
                    <Td>
                      {customer.lastDepositAt
                        ? formatDate(customer.lastDepositAt, format)
                        : <span className="text-muted">From a return</span>}
                    </Td>
                    <Td className="text-right font-medium">{formatMoney(customer.credit, format)}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pagination
              page={held.data.meta.page}
              totalPages={held.data.meta.totalPages}
              total={held.data.meta.total}
              onPageChange={setPage}
            />
          </>
        )}
      </Card>

      <Card>
        <div className="border-b border-subtle p-4">
          <h2 className="text-sm font-semibold">Deposits taken</h2>
          <p className="text-xs text-muted">Newest first. A voided deposit is not listed.</p>
        </div>
        {recent.isLoading ? (
          <LoadingState />
        ) : !recent.data || recent.data.items.length === 0 ? (
          <EmptyState icon={HandCoins} title="No deposits yet" />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Date</Th>
                  <Th>Number</Th>
                  <Th>Customer</Th>
                  <Th>Method</Th>
                  <Th>Reference</Th>
                  <Th className="text-right">Amount</Th>
                </tr>
              </thead>
              <tbody>
                {recent.data.items.map((payment) => (
                  <tr key={payment.id}>
                    <Td>{formatDate(payment.receivedAt, format)}</Td>
                    <Td>{payment.number}</Td>
                    <Td>{payment.customer?.name ?? '—'}</Td>
                    <Td>{payment.method.name}</Td>
                    <Td>{payment.reference ?? <span className="text-muted">—</span>}</Td>
                    <Td className="text-right font-medium">{formatMoney(payment.amount, format)}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pagination
              page={recent.data.meta.page}
              totalPages={recent.data.meta.totalPages}
              total={recent.data.meta.total}
              onPageChange={setRecentPage}
            />
          </>
        )}
      </Card>

      <Modal
        open={recording}
        onClose={close}
        title="Record deposit"
        description="The money goes into the account below and stays on the customer's account until they spend it."
        footer={
          <>
            <Button variant="ghost" onClick={close}>Cancel</Button>
            <Button
              onClick={() => void submit()}
              loading={createState.isLoading}
              disabled={
                !form.customerId
                || !(Number(form.amount) > 0)
                || !form.methodId
                || (chosenMethod?.requiresReference && !form.reference)
              }
            >
              Record deposit
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {failure && <Alert tone="danger">{failure}</Alert>}

          <CustomerPicker
            required
            value={form.customerId}
            onChange={(customerId) => setForm({ ...form, customerId })}
            placeholder="Select a customer"
            hint="A deposit belongs to someone — that is who can spend it."
          />

          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              label="Amount"
              type="number"
              min="0"
              required
              value={form.amount}
              onChange={(event) => setForm({ ...form, amount: event.target.value })}
            />
            <Select
              label="Paid by"
              required
              value={form.methodId}
              onChange={(event) => setForm({ ...form, methodId: event.target.value })}
            >
              <option value="">Select a method</option>
              {takingMethods.map((method) => (
                <option key={method.id} value={method.id}>{method.name}</option>
              ))}
            </Select>
            <Select
              label="Into account"
              value={form.accountId}
              onChange={(event) => setForm({ ...form, accountId: event.target.value })}
              hint="Blank uses the method's own account."
            >
              <option value="">{chosenMethod?.account?.name ?? 'Default'}</option>
              {accounts?.items
                .filter((account) => account.isActive)
                .map((account) => (
                  <option key={account.id} value={account.id}>{account.name}</option>
                ))}
            </Select>
            <Input
              label="Reference"
              required={chosenMethod?.requiresReference}
              value={form.reference}
              onChange={(event) => setForm({ ...form, reference: event.target.value })}
              hint="For a transfer or mobile money."
            />
          </div>

          <Input
            label="Note"
            placeholder="e.g. towards iron sheets for the new house"
            value={form.notes}
            onChange={(event) => setForm({ ...form, notes: event.target.value })}
          />
        </div>
      </Modal>
    </>
  );
}
